// What lives in the backdrop: the buildings and trees of the mid and near bands (placed by a
// seeded layout, so every player sees the same landscape) with their attachments — turning
// windmill sails, chimney smoke, lit windows at night, trees swaying in the wind — plus
// drifting clouds, passing birds and fireflies along the hedge at night.
import Phaser from 'phaser';
import { layoutBand, nudgeLayout, repeatPositions, bandX, snapPx, CLOUD_ABOVE, BIRD_FLIGHT, NEAR_TREES, MID_OBJECTS } from './backdropLayout.mjs';
import { bandLight, starAlpha } from './skyClock.mjs';
import { mulberry32 } from './rng.mjs';
import { BD } from './backdropAnchors.mjs';
import { DEPTH } from './backdropDepth.mjs';
import { subpixel } from './subpixel.js';
import { audioEvents } from '../audio/audioEvents.js';

const MARGIN = 120;
const TREE_KEYS = [0, 1, 2, 3].map((i) => `farm_bd_tree_${i}`);
const BAND_OBJECTS = {
  mid: {
    // baseUp is measured from the band's own bottom edge: the mid objects stand on the part of
    // the fields that shows above the near hedge.
    seed: 4111, span: MID_OBJECTS.span, nudges: MID_OBJECTS.nudges, scaleTree: 0.6, baseUp: 18, sway: 0.015,
    items: [
      { key: 'farm_bd_barn' }, { key: 'farm_bd_farmhouse' }, { key: 'farm_bd_windmill' },
      { key: 'farm_bd_silo' }, { key: 'farm_bd_silo' },
      ...Array.from({ length: 6 }, (_, i) => ({ key: TREE_KEYS[i % 4], tree: true, flippable: true, jitterY: 8 })),
    ],
  },
  near: {
    seed: 8123, span: 2000, scaleTree: NEAR_TREES.scale, baseUp: NEAR_TREES.baseUp, sway: 0.03,
    items: Array.from({ length: 10 }, (_, i) => ({ key: TREE_KEYS[(i * 3) % 4], tree: true, flippable: true, jitterY: 6 })),
  },
};
const COPIES = 3;
const SMOKE_EVERY_MS = 800;
const SMOKE_LIFE_MS = 3200;
const TURN_MS = 6000;
const glow = (a) => Math.min(1, Math.max(0, (a - 0.2) / 0.6));

// ---- band objects -------------------------------------------------------------------
export function createBandObjects(scene, bands) {
  const has = (k) => scene.textures.exists(k);
  const groups = [];
  for (const [band, spec] of Object.entries(BAND_OBJECTS)) {
    const items = spec.items.filter((it) => has(it.key)).map((it) => {
      const scale = it.tree ? spec.scaleTree : 1;
      return { ...it, scale, w: BD[it.key].w * scale };
    });
    if (!items.length) continue;
    const layout = nudgeLayout(layoutBand(items, spec.seed, spec.span), spec.nudges ?? {});
    const depth = band === 'mid' ? DEPTH.midObjects : DEPTH.nearObjects;
    // Like the band strips: one container at the band's sub-pixel offset, objects on whole
    // pixels inside it, so houses and trees glide with their hills instead of stepping.
    const box = scene.add.container(0, 0).setScrollFactor(0).setDepth(depth);
    const entries = layout.map((o, idx) => {
      const a = BD[o.key];
      const tree = o.key.startsWith('farm_bd_tree_');
      const origin = tree && a.trunk ? a.trunk : (a.base ?? [0.5, 1]);
      const make = () => scene.add.image(0, 0, o.key).setOrigin(origin[0], origin[1])
        .setScale(0.5 * o.scale).setFlipX(o.flipX).setVisible(false);
      const add = (obj) => { if (obj) box.add(obj); return obj; };   // container order = draw order
      return Array.from({ length: COPIES }, () => ({
        img: add(make()),
        veil: add(band === 'mid' ? make() : null),
        blades: add(o.key === 'farm_bd_windmill' && a.hub && has('farm_bd_blades')
          ? scene.add.image(0, 0, 'farm_bd_blades').setScale(0.5).setVisible(false) : null),
        windows: (a.windows ?? []).map(() => {
          const r = scene.add.rectangle(0, 0, 1, 1, 0xffd27a, 0).setOrigin(0, 0);
          r.blendMode = Phaser.BlendModes.ADD;
          return add(r);
        }),
        smoke: o.key === 'farm_bd_farmhouse' && a.chimney ? { puffs: [], last: 0 } : null,
        swayPhase: (idx * 1.7) % (Math.PI * 2),
        o, a, tree, origin, box,
      }));
    });
    groups.push({ band, spec, entries, box });
  }

  // A point of the sprite (fractions) in screen px, given where its origin was placed.
  const pointOf = (e, x, y, fx, fy, s = 1) => {
    const w = e.a.w * e.o.scale * s, h = e.a.h * e.o.scale * s;
    const sx = e.o.flipX ? -1 : 1;
    return { x: x + sx * (fx - e.origin[0]) * w, y: y + (fy - e.origin[1]) * h };
  };

  const updateSmoke = (e, x, y, ctx, night) => {
    const s = e.smoke;
    if (!s) return;
    const c = pointOf(e, x, y, e.a.chimney[0], e.a.chimney[1]);
    if (!night && ctx.now - s.last > SMOKE_EVERY_MS) {
      s.last = ctx.now;
      const puff = scene.add.circle(c.x, c.y, 3, 0xeeeeee, 0.6);
      e.box.add(puff);
      s.puffs.push({ obj: puff, born: ctx.now, x0: c.x - x, drift: 4 + Math.random() * 6 });
    }
    s.puffs = s.puffs.filter((p) => {
      const f = (ctx.now - p.born) / SMOKE_LIFE_MS;
      if (f >= 1) { p.obj.destroy(); return false; }
      p.obj.setPosition(x + p.x0 + f * p.drift, c.y - f * 26).setRadius(3 + f * 7).setAlpha(0.55 * (1 - f));
      return true;
    });
  };

  return {
    update(ctx) {
      const geom = bands?.bandGeometry(ctx);
      if (!geom) return;
      const stars = starAlpha(ctx.t);
      const lit = glow(stars);
      const night = stars > 0.5;
      for (const g of groups) {
        const { bottom, factor, scale: s, fx, fy } = geom[g.band];   // s: the near band scales with the zoom
        g.box.setPosition(fx, fy);
        const light = bandLight(ctx.t, g.band);
        for (const copies of g.entries) {
          const e0 = copies[0];
          // Positioned like the band's own strip (the near band zooms about the screen's middle).
          const xs = repeatPositions(bandX(g.band, e0.o.x, ctx.panX, ctx.zoom, ctx.viewW / 2, factor), g.spec.span * s, ctx.viewW, MARGIN);
          copies.forEach((e, i) => {
            const x = xs[i] === undefined ? undefined : snapPx(xs[i] - fx);   // container-local, whole px
            const show = x !== undefined;
            e.img.setVisible(show);
            e.veil?.setVisible(show);
            e.blades?.setVisible(show);
            for (const w of e.windows) w.setVisible(show);
            if (!show) { if (e.smoke) { for (const p of e.smoke.puffs) p.obj.destroy(); e.smoke.puffs = []; } return; }
            const y = snapPx(bottom - fy - (g.spec.baseUp + e.o.yOffset) * s);
            const rot = e.tree ? g.spec.sway * Math.sin(ctx.now / 1300 + e.swayPhase) : 0;
            e.img.setScale(0.5 * e.o.scale * s).setPosition(x, y).setRotation(rot).setTint(light.tint);
            e.veil?.setScale(0.5 * e.o.scale * s).setPosition(x, y).setRotation(rot).setTintFill(light.haze).setAlpha(light.hazeAlpha);
            if (e.blades) {
              const h = pointOf(e, x, y, e.a.hub[0], e.a.hub[1], s);
              const turn = (ctx.now / TURN_MS) * Math.PI * 2 + 0.3 * Math.sin(ctx.now / 1700);
              e.blades.setScale(0.5 * s).setPosition(h.x, h.y).setRotation(turn).setTint(light.tint);
            }
            e.a.windows?.forEach(([fx, fy, fw, fh], k) => {
              const p = pointOf(e, x, y, e.o.flipX ? fx + fw : fx, fy, s);
              e.windows[k].setPosition(p.x, p.y).setSize(fw * e.a.w * e.o.scale * s, fh * e.a.h * e.o.scale * s).setFillStyle(0xffd27a, 0.85 * lit);
            });
            updateSmoke(e, x, y, ctx, night);
          });
        }
      }
    },
    destroy() {
      for (const g of groups) g.box.destroy(true);
    },
  };
}

// ---- clouds (soft, outline-free art) --------------------------------------------------
// High above the hills, riding on the horizon like the rest of the backdrop (CLOUD_ABOVE).
// Each sits in a sub-pixel container, so a 4 px/s drift is smooth.
const CLOUD_SPAN = 3000;
// `rows`: how high above the horizon the far and near rows ride (the street's sky sits higher than the farm's).
export function createClouds(scene, { rows = CLOUD_ABOVE } = {}) {
  const keys = [0, 1, 2, 3, 4].map((i) => `farm_bd_cloud_${i}`).filter((k) => scene.textures.exists(k));
  const r = mulberry32(777);
  const clouds = [];
  const add = (n, depth, speed, factor, scale, row) => {
    for (let i = 0; i < n && keys.length; i++) {
      const img = scene.add.image(0, 0, keys[Math.floor(r() * keys.length)]).setScale(0.5 * scale).setAlpha(0.95);
      clouds.push({
        img, box: subpixel(scene, img, depth),
        x0: r() * CLOUD_SPAN, above: row.min + r() * (row.max - row.min), speed, factor,
      });
    }
  };
  add(5, DEPTH.farClouds, 4, 0.08, 0.6, rows.far);
  add(4, DEPTH.nearClouds, 9, 0.18, 1, rows.near);
  return {
    update(ctx) {
      // Lit like the far landscape: white by day, warm at dusk, dark blue at night.
      const tint = bandLight(ctx.t, 'far').tint;
      for (const c of clouds) {
        const raw = c.x0 + (ctx.now / 1000) * c.speed + ctx.panX * c.factor;
        const x = ((raw % CLOUD_SPAN) + CLOUD_SPAN) % CLOUD_SPAN - (CLOUD_SPAN - ctx.viewW) / 2;
        c.box.setPosition(x, ctx.horizonY - c.above);
        c.img.setTint(tint);
      }
    },
    destroy() { for (const c of clouds) c.box.destroy(); },
  };
}

// ---- birds --------------------------------------------------------------------------
// A small flock every 20–40 s by day. They fly at their own pace (BIRD_FLIGHT) with a gentle
// parallax of their own, so walking under them neither freezes nor races them; vertically they
// ride on the horizon with the rest of the backdrop.
export function createBirds(scene) {
  const frames = ['farm_bd_bird_0', 'farm_bd_bird_1'];
  const ok = frames.every((k) => scene.textures.exists(k));
  let flock = [];
  let nextAt = 0;
  const clear = () => { for (const b of flock) b.box.destroy(); flock = []; };
  const spawn = (ctx) => {
    const n = 3 + Math.floor(Math.random() * 3);
    const dir = Math.random() < 0.5 ? 1 : -1;
    const speed = BIRD_FLIGHT.speedMin + Math.random() * (BIRD_FLIGHT.speedMax - BIRD_FLIGHT.speedMin);
    const above = BIRD_FLIGHT.aboveMin + Math.random() * (BIRD_FLIGHT.aboveMax - BIRD_FLIGHT.aboveMin);
    const x0 = dir > 0 ? -60 : ctx.viewW + 60;
    audioEvents.emit('birds');                     // they chirp as they pass (owner 2026-10-03)
    flock = Array.from({ length: n }, (_, i) => {
      const img = scene.add.image(0, 0, frames[0]).setScale(0.5).setFlipX(dir < 0);   // the art faces right
      return {
        img, box: subpixel(scene, img, DEPTH.birds),
        x: x0 - dir * i * 22, dy: (i % 2) * 9 + i * 3, above, dir, speed, phase: i * 0.7, pan0: ctx.panX,
      };
    });
  };
  return {
    update(ctx) {
      if (!ok) return;
      const day = ctx.t >= 90 && ctx.t < 210;
      if (!flock.length && day && ctx.now >= nextAt) {
        if (nextAt) spawn(ctx);
        nextAt = ctx.now + 20000 + Math.random() * 20000;
      }
      const dt = Math.min(ctx.dtMs, 100) / 1000;   // a hidden tab must not fling them off screen
      let away = 0;
      for (const b of flock) {
        b.x += b.dir * b.speed * dt;
        const sx = b.x + (ctx.panX - b.pan0) * BIRD_FLIGHT.factor;
        b.box.setPosition(sx, ctx.horizonY - b.above + b.dy + 3 * Math.sin(ctx.now / 400 + b.phase));
        b.img.setTexture(frames[Math.floor((ctx.now + b.phase * 300) / 150) % 2]);
        if (sx < -80 || sx > ctx.viewW + 80) away++;
      }
      if (flock.length && away === flock.length && flock.every((b) => (b.dir > 0 ? b.x > 0 : b.x < ctx.viewW))) clear();
    },
    destroy() { clear(); },
  };
}

// ---- fireflies ----------------------------------------------------------------------
export function createFireflies(scene, count = 18) {
  const r = mulberry32(99);
  const flies = Array.from({ length: count }, () => {
    const c = scene.add.circle(0, 0, 1.6, 0xfff7a0, 0).setScrollFactor(0).setDepth(DEPTH.fireflies);
    c.blendMode = Phaser.BlendModes.ADD;
    return { c, u: r(), dy: -30 + r() * 40, ph: r() * Math.PI * 2, sp: 0.5 + r() };
  });
  return {
    update(ctx) {
      const a = Math.max(0, (starAlpha(ctx.t) - 0.5) * 2);
      for (const f of flies) {
        f.c.setVisible(a > 0.01);
        if (a <= 0.01) continue;
        const s = ctx.now / 1000 * f.sp + f.ph;
        f.c.setPosition(f.u * ctx.viewW + 14 * Math.sin(s * 0.7), ctx.horizonY + f.dy + 6 * Math.sin(s * 1.3))
          .setAlpha(a * (0.35 + 0.65 * Math.max(0, Math.sin(s * 2.1))));
      }
    },
    destroy() { for (const f of flies) f.c.destroy(); },
  };
}
